import { resolveRpcFromApiVersions } from '@/utility/apiVersionMatrix'
import {
  normalizeTagsCatalogById,
  tagsArrayToById,
  tagsByIdToList,
  toTagId,
} from './normalizeCatalogTag'
import { getShipmentTagIds, resolveShipmentAssignedTags } from './normalizeShipmentAssignedTags'
import {
  clearAllTagsCatalogFromIdb,
  loadTagsCatalogFromIdb,
  removeTagsCatalogFromIdb,
  saveTagsCatalogToIdb,
} from './tagsCatalogIdb'
import {
  clearAllTagsInMemory,
  clearOrgTagsInMemory,
  getOrgTagsById,
  getTagByIdFromMemory,
  getTagsListFromMemory,
  removeTagFromMemory,
  setOrgPinnedTagGroupNameInMemory,
  setOrgTagsByIdInMemory,
  subscribeOrgTagsInMemory,
  upsertTagInMemory,
} from './tagsCatalogMemory'

const inFlightSyncByOrg = new Map()
const syncedOrgIds = new Set()

const TAGS_SELECT_COLUMNS = 'id, tag_name, group_name, color_code, deleted'

const trimOrgId = (orgId) => String(orgId || '').trim()

/**
 * Write the org pinned tag group name into memory (or clear it).
 * Used by Organization settings after a successful pinned-group save when CC-6037 is on.
 * @param {string | null | undefined} orgId
 * @param {string | null | undefined} groupName
 */
export function applyOrgPinnedTagGroupName(orgId, groupName) {
  const trimmedOrgId = trimOrgId(orgId)
  if (!trimmedOrgId) return
  const nextName = String(groupName || '').trim() || null
  setOrgPinnedTagGroupNameInMemory(trimmedOrgId, nextName)
}

/**
 * Refresh the org's pinned tag group name from Landing Zone.
 * Sets memory to the group with `pinned === true`, or clears it when none is pinned.
 * Best-effort: catalog sync continues if this RPC fails.
 * @param {object} client Supabase client with `rpc`
 * @param {string | null | undefined} orgId
 */
export async function refreshOrgPinnedTagGroupName(client, orgId) {
  const trimmedOrgId = trimOrgId(orgId)
  if (!client?.rpc || !trimmedOrgId) return

  try {
    const { data, error } = await client.rpc(resolveRpcFromApiVersions('get_org_tag_groups_v1'), {
      p_org_id: trimmedOrgId,
    })

    if (error || !Array.isArray(data)) return

    const pinnedGroup = data.find((group) => group?.pinned === true && group?.tag_group_name)
    setOrgPinnedTagGroupNameInMemory(trimmedOrgId, pinnedGroup?.tag_group_name || null)
  } catch (error) {
    console.error('Failed to load pinned tag group:', error)
  }
}

export async function fetchOrganizationTags(client, orgId) {
  if (!client?.from) {
    return { ok: false, error: 'Tags catalog fetch is not available.', data: {} }
  }

  const trimmedOrgId = String(orgId || '').trim()
  if (!trimmedOrgId) {
    return { ok: false, error: 'Organization id is required.', data: {} }
  }

  try {
    const { data, error } = await client
      .from('tags')
      .select(TAGS_SELECT_COLUMNS)
      .eq('org_id', trimmedOrgId)

    if (error) {
      return {
        ok: false,
        error: error.message || 'Failed to load organization tags.',
        data: {},
      }
    }

    const tagsById = tagsArrayToById(data || [])
    return { ok: true, data: tagsById, error: null }
  } catch (error) {
    return {
      ok: false,
      error: error?.message || 'Failed to load organization tags.',
      data: {},
    }
  }
}

/**
 * Load cached tags from IndexedDB into memory, then refresh from Supabase.
 */
export async function hydrateTagsCatalog(client, orgId, options = {}) {
  const trimmedOrgId = String(orgId || '').trim()
  if (!trimmedOrgId) {
    return { ok: false, error: 'Organization id is required.', data: {} }
  }

  const cached = await loadTagsCatalogFromIdb(trimmedOrgId)
  if (cached?.tagsById && Object.keys(cached.tagsById).length > 0) {
    setOrgTagsByIdInMemory(trimmedOrgId, cached.tagsById)
  }

  const shouldForceSync = Object.keys(getOrgTagsById(trimmedOrgId)).length === 0
  return syncTagsCatalog(client, trimmedOrgId, { ...options, force: shouldForceSync })
}

/**
 * Fetch all organization tags from Supabase and update memory + IndexedDB.
 */
export async function syncTagsCatalog(
  client,
  orgId,
  { force = false, refreshPinnedGroupOnCachedSync = true } = {},
) {
  const trimmedOrgId = String(orgId || '').trim()
  if (!trimmedOrgId) {
    return { ok: false, error: 'Organization id is required.', data: {} }
  }

  const existingBeforeSync = getOrgTagsById(trimmedOrgId)
  const existingCountBeforeSync = Object.keys(existingBeforeSync).length

  if (!force && syncedOrgIds.has(trimmedOrgId)) {
    if (existingCountBeforeSync > 0) {
      // Initial load already fetched the pinned group. Later cached syncs skip
      // get_org_tag_groups_v1 when the caller opts out; realtime refreshes it.
      if (refreshPinnedGroupOnCachedSync) {
        await refreshOrgPinnedTagGroupName(client, trimmedOrgId)
      }
      return { ok: true, data: existingBeforeSync, error: null }
    }
    syncedOrgIds.delete(trimmedOrgId)
  }

  if (inFlightSyncByOrg.has(trimmedOrgId)) {
    return inFlightSyncByOrg.get(trimmedOrgId)
  }

  const syncPromise = fetchOrganizationTags(client, trimmedOrgId)
    .then(async (result) => {
      if (result.ok) {
        const incomingTags = result.data || {}
        const existingTags = getOrgTagsById(trimmedOrgId)
        const incomingCount = Object.keys(incomingTags).length
        const existingCount = Object.keys(existingTags).length

        // A wrong-database fetch (e.g. Command Center) can return zero rows and wipe
        // a catalog that Header already hydrated from Landing Zone.
        if (incomingCount === 0 && existingCount > 0) {
          await refreshOrgPinnedTagGroupName(client, trimmedOrgId)
          syncedOrgIds.add(trimmedOrgId)
          return { ...result, data: existingTags }
        }

        if (incomingCount > 0) {
          setOrgTagsByIdInMemory(trimmedOrgId, incomingTags)
          await saveTagsCatalogToIdb(trimmedOrgId, incomingTags)
          await refreshOrgPinnedTagGroupName(client, trimmedOrgId)
          syncedOrgIds.add(trimmedOrgId)
          return { ...result, data: incomingTags }
        }

        // Empty fetch with an empty in-memory catalog: do not persist or mark synced.
      }
      return result
    })
    .finally(() => {
      inFlightSyncByOrg.delete(trimmedOrgId)
    })

  inFlightSyncByOrg.set(trimmedOrgId, syncPromise)
  return syncPromise
}

export function removeTag(orgId, tagId) {
  if (!orgId || !tagId) return
  removeTagFromMemory(orgId, tagId)
  saveTagsCatalogToIdb(orgId, getOrgTagsById(orgId))
}

export function upsertTag(orgId, tag) {
  const tagId = toTagId(tag)
  if (!tagId) return

  upsertTagInMemory(orgId, tag)
  saveTagsCatalogToIdb(orgId, getOrgTagsById(orgId))
}

export async function clearOrgTagsCatalog(orgId) {
  if (!orgId) return
  const trimmedOrgId = String(orgId).trim()
  syncedOrgIds.delete(trimmedOrgId)
  clearOrgTagsInMemory(orgId)
  await removeTagsCatalogFromIdb(orgId)
}

/**
 * Wipe all org catalogs from memory, IndexedDB, and in-flight sync state.
 * Call on sign-out, org switch, and impersonation session changes.
 */
export async function clearAllTagsCatalog() {
  inFlightSyncByOrg.clear()
  syncedOrgIds.clear()
  clearAllTagsInMemory()
  await clearAllTagsCatalogFromIdb()
}

export function getTagById(orgId, tagId) {
  return getTagByIdFromMemory(orgId, tagId)
}

/** Id-keyed catalog map for O(1) lookups without array scans. */
export function getTagsById(orgId) {
  return getOrgTagsById(orgId)
}

/** Flat TagMeta list for pickers, filters, and legacy consumers. */
export function getTagsList(orgId) {
  return getTagsListFromMemory(orgId).filter((tag) => !tag.deleted)
}

/**
 * Derive unique tag group names from the in-memory org catalog.
 * Used by list configuration when CC-6037 tag pivot is enabled.
 * @param {string | null | undefined} orgId
 * @param {{ sortBy?: string, sortOrder?: 'asc' | 'desc' }} [options]
 * @returns {Array<{ group_name: string }>} Sorted unique tag group names for secondary list grouping.
 */
export function getTagGroupsFromCatalog(orgId, { sortBy = 'name', sortOrder = 'desc' } = {}) {
  void sortBy

  const groups = new Map()

  getTagsListFromMemory(trimOrgId(orgId)).forEach((tag) => {
    if (tag.deleted) return

    const groupName = tag.group_name
    if (groupName) {
      groups.set(groupName, { group_name: groupName })
    }
  })

  const list = Array.from(groups.values())
  list.sort((left, right) => {
    const cmp = left.group_name.localeCompare(right.group_name, undefined, { sensitivity: 'base' })
    return sortOrder === 'desc' ? -cmp : cmp
  })

  return list
}

export function subscribeTagsCatalog(orgId, listener) {
  return subscribeOrgTagsInMemory(orgId, listener)
}

export { getShipmentTagIds, normalizeTagsCatalogById, resolveShipmentAssignedTags, tagsByIdToList }

export const resetTagsCatalogServiceForTests = () => {
  inFlightSyncByOrg.clear()
  syncedOrgIds.clear()
  clearAllTagsInMemory()
}
